import {useEffect, useState} from 'react';

import Button from 'components/Button/Button';
import FormControl from 'components/FormControl/FormControl';
import FormTextarea from 'components/FormTextarea/FormTextarea';
import PhotoUploader from 'components/PhotoUploader/PhotoUploader';
import {Form, Formik} from 'formik';
import {useGlobalState} from 'hooks/useGlobalState';
import LayoutPage from 'layouts/LayoutPage/LayoutPage';
import {useDispatch} from 'react-redux';
import {useNavigate, useParams} from 'react-router';
import {getSingleEditorACS, updateEditorACS} from 'store/singlePageEditor/requestAC';
import {loosePhoneRegexp} from 'utils/helpers/validators/loosePhoneRegexp';
import * as yup from 'yup';

import './PageEditorsEdit.scss';
import ProfileImage from './ProfileImage/ProfileImage';

interface FormValues {
  profileImageSignedId: undefined | string;
  nickname: string;
  email: string;
  phoneNumber: string;
  adminNote: string;
}

const validationSchema = yup.object({
  nickname: yup.string(),
  email: yup.string().email('Email is not valid').required('Email is required'),
  phoneNumber: yup.string().matches(loosePhoneRegexp, 'Invalid phone number'),
});

const PageEditorsEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const {id} = useParams();

  const {singleEditor} = useGlobalState((state) => state.singlePageEditor);

  useEffect(() => {
    if (id) dispatch(getSingleEditorACS(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEditPageEditor = (values: FormValues) => {
    if (!singleEditor) return;

    dispatch(
      updateEditorACS({
        id: singleEditor.id,
        data: {
          nickname: values.nickname,
          email: values.email.trim(),
          phone: values.phoneNumber,
          profile_image: values.profileImageSignedId,
          admin_note: values.adminNote,
        },
      }),
    );
  };

  return (
    <LayoutPage>
      <div className="page-editors-edit">
        <div className="page-editors-edit__container">
          {singleEditor ? (
            <Formik
              enableReinitialize
              initialValues={{
                profileImageSignedId: undefined,
                nickname: singleEditor.attributes.nickname || '',
                email: singleEditor.attributes.email,
                phoneNumber: singleEditor.attributes.phone || '',
                adminNote: singleEditor.attributes.admin_note || '',
              }}
              onSubmit={handleEditPageEditor}
              validationSchema={validationSchema}
            >
              {({setFieldValue, values, errors}) => (
                <Form className="page-editors-edit__form">
                  <h1 className="page-editors-edit__title">Editor Information</h1>
                  {isUploadingPhoto ? (
                    <PhotoUploader
                      setIsPhotoChanging={() => {}}
                      title="Upload"
                      onCancel={() => setIsUploadingPhoto(false)}
                      onSetPhoto={(imageBlobSignedId: string) =>
                        setFieldValue('profileImageSignedId', imageBlobSignedId)
                      }
                    />
                  ) : (
                    <>
                      <div className="page-editors-edit__form-wrapper">
                        <div className="page-editors-edit__profile-image-wrapper">
                          <h4 className="page-editors-edit__profile-image-title">Profile Photo</h4>
                          <ProfileImage singleEditor={singleEditor} onEdit={() => setIsUploadingPhoto(true)} />
                        </div>
                        <div className="page-editors-edit__form-container">
                          <div className="page-editors-edit__inputs-container">
                            <div
                              className={`page-editors-edit__form-control ${
                                errors.nickname ? 'page-editors-edit__form-control-error' : ''
                              }`}
                            >
                              <FormControl
                                value={values.nickname}
                                onChange={(val) => setFieldValue('nickname', val)}
                                error={errors.nickname}
                                placeholder="Enter nickname"
                                label="Nickname"
                              />
                            </div>
                            <div
                              className={`page-editors-edit__form-control ${
                                errors.email ? 'page-editors-edit__form-control--error' : ''
                              }`}
                            >
                              <FormControl
                                value={values.email}
                                onChange={(val) => setFieldValue('email', val, true)}
                                error={errors.email}
                                placeholder="Enter email"
                                label="Email"
                              />
                            </div>
                            <div
                              className={`page-editors-edit__form-control ${
                                errors.phoneNumber ? 'page-editors-edit__form-control-error' : ''
                              }`}
                            >
                              <FormControl
                                value={values.phoneNumber}
                                onChange={(val) => setFieldValue('phoneNumber', val)}
                                error={errors.phoneNumber}
                                placeholder="+381"
                                label="Phone Number"
                              />
                            </div>
                          </div>
                          <div className="page-editors-edit__note">
                            <div className="page-editors-edit__form-control">
                              <FormTextarea
                                label="Note"
                                placeholder="Admin note"
                                value={values.adminNote}
                                onChange={(val) => setFieldValue('adminNote', val)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="page-editors-edit__actions">
                        <Button
                          onClick={() => navigate(-1)}
                          className="page-editors-edit__cancel"
                          variant="tertiary-outlined"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="page-editors-edit__save-changes" variant="tertiary">
                          Save Changes
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              )}
            </Formik>
          ) : (
            <div className="page-editors-edit__loader">
              <div className="spinner-border" />
            </div>
          )}
        </div>
      </div>
    </LayoutPage>
  );
};

export default PageEditorsEdit;
